<script setup lang="ts">
import { formatNumberFullTruncate, middleTruncate } from '@/helpers/utils'
import BalanceCurrencyImage from '@/assets/images/balance-currency.png'
import TonSymbol from '@/assets/images/ton-symbol.png'
import { useWalletConnect } from '@/composables/game/wallet-connect'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import { T, useTranslate } from '@tolgee/vue'
import router from '@/router'
import { RouteName } from '@/routes'
import CopyIcon from '@/components/icons/CopyIcon.vue'
import CheckCircleGreenBorder from '@/components/icons/CheckCircleGreenBorder.vue'
import { useClipboard } from '@/composables/helpers/clipboard'
import BaseButton from '@/components/base/BaseButton.vue'
import { computed, ref } from 'vue'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import WithdrawModal from '@/components/modals/WithdrawModal.vue'
import WithdrawOverlay from '@/components/overlays/WithdrawOverlay.vue'
import { DateTime } from 'luxon'
import { useNow } from '@/composables/game/time'

const { userFriendlyAddress } = useWalletConnect()
const { copyToClipboard } = useClipboard()
const { t } = useTranslate()
const { currentBalance } = useFortuneWheel()
const { open } = useTonConnectModal()
const { now } = useNow()

function copyWalletAddress() {
  copyToClipboard(userFriendlyAddress.value, t.value('wallet.wallet_address_copied', 'Wallet address copied!'))
}

const limitNotReached = computed(() => currentBalance.value <= 10)
const disabledBecauseOfSnapshot = computed(() => {
  const targetDate = DateTime.fromISO('2025-12-22T08:00:00Z', { zone: 'utc' }).toSeconds()
  return now.value >= targetDate
})

const showWithdrawModal = ref<boolean>(false)
const showWithdrawOverlay = ref<boolean>(false)

const withdrawAmount = ref<number>(currentBalance.value)

</script>
<template>
  <div class="flex flex-1 flex-col justify-between items-center w-full h-full">
    <div class="flex flex-col w-full gap-y-3">
      <div
        class="flex items-center py-[9px] pl-3 pr-4 bg-dark-bottom-layer w-full rounded-xl text-2xl font-semibold -tracking-[0.48px]"
      >
        <div class="flex items-center justify-between size-[42px] relative mr-3">
          <img
            :src="BalanceCurrencyImage"
            class="w-full object-contain"
          >
          <div class="size-3.5 absolute bottom-0 right-0">
            <img :src="TonSymbol">
          </div>
        </div>
        <span class="mr-2">{{ formatNumberFullTruncate(currentBalance, true) }}</span>
        <span class="text-white/60">USDT</span>
      </div>
      <div
        v-if="userFriendlyAddress"
        class="flex justify-between w-full py-[18px] px-4 bg-dark-top-layer rounded-xl"
      >
        <div class="flex items-center gap-x-2">
          <div class="text-base font-semibold">
            {{ middleTruncate(userFriendlyAddress, 11) }}
          </div>
          <CopyIcon
            class="w-4 h-4 min-w-[16px]"
            @click="copyWalletAddress"
          />
        </div>
        <div class="flex items-center gap-x-2">
          <CheckCircleGreenBorder
            wrapper-class="w-4 h-4 min-w-[16px]"
            icon-class="w-2"
          />
          <div class="text-green-primary text-sm font-semibold">
            <T
              key-name="wallet.connected"
              default-value="Connected"
            />
          </div>
        </div>
      </div>
      <BaseLinkButton
        v-if="userFriendlyAddress"
        @click="router.push(RouteName.PROFILE)"
      >
        <T
          key-name="balance.manage_wallet"
          default-value="Manage wallet"
        />
      </BaseLinkButton>
    </div>
    <div class="flex flex-col w-full gap-y-3 flex-1 justify-end">
      <div
        class="flex items-center flex-col gap-y-3 w-full"
      >
        <div class="text-center text-white/60 text-base font-medium">
          <T
            key-name="balance.withdraw.explanation"
            default-value="Withdrawal available over 10 USDT"
          />
        </div>
        <BaseButton
          v-if="!userFriendlyAddress"
          class="w-full"
          @click="open"
        >
          <template
            #leftIcon
          >
            <img
              src="@/assets/images/gifs/money.gif"
              class="h-full w-auto max-w-[24px]"
            >
          </template>
          <T
            key-name="wallet.connect_wallet"
            default-value="Connect wallet"
          />
        </BaseButton>
      </div>
      <BaseButton
        style-type="tertiary"
        @click="router.back()"
      >
        <T
          key-name="balance.close"
          default-value="Close"
        />
      </BaseButton>
    </div>
  </div>

  <WithdrawModal
    v-if="showWithdrawModal"
    :show="showWithdrawModal"
    @hide="showWithdrawModal = false"
    @after-confirm="showWithdrawOverlay = true"
  />
  <WithdrawOverlay
    v-if="showWithdrawOverlay"
    :class="showWithdrawOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
    :show="showWithdrawOverlay"
    :amount="withdrawAmount"
    :wallet-adress="userFriendlyAddress"
    @click="showWithdrawOverlay = false"
  />
</template>
